import { useCallback, useEffect, useMemo, useState } from 'react';
import { HStack } from '@cad-arc';
import { EventPanelFormOpenProQaButtons } from './event-panel-form-open-proqa-buttons';
import { EventPanelFormOpenProQaSummaryButton } from './event-panel-form-open-proqa-summary-button';
import { Discipline } from './helpers/pro-qa-buttons-helpers';
import {
    getProQaTempIncidentStorageKeys,
    getResolvedTemporaryIncidentIds,
    migrateTemporaryIncidentIdsToSavedEvent,
    setResolvedTemporaryIncidentId,
} from './helpers/pro-qa-temp-incident-storage';
import { useWorkspacePanel } from '../../panels/hooks/use-workspace-panel';

const EMPTY_INCIDENT_IDS = {
    POLICE: undefined,
    FIRE: undefined,
    EMS: undefined,
};

export function EventPanelFormProQaButtons({
    policeInstanceEnabled,
    fireInstanceEnabled,
    emsInstanceEnabled,
    enhancementsEnabled,
    isDraftEvent,
    setCurrentTempId,
}: {
    policeInstanceEnabled?: boolean;
    fireInstanceEnabled?: boolean;
    emsInstanceEnabled?: boolean;
    enhancementsEnabled?: boolean;
    isDraftEvent?: boolean;
    setCurrentTempId: (id?: string) => void;
}) {
    const workspacePanel = useWorkspacePanel<'EVENT'>();
    const proQaTempIncidentStorageKeys = useMemo(
        () =>
            getProQaTempIncidentStorageKeys({
                workspacePanelInitialId: workspacePanel.initialId,
                eventId: workspacePanel.linkedEntityId,
            }),
        [workspacePanel.initialId, workspacePanel.linkedEntityId]
    );
    const enabledDisciplines = useMemo(() => {
        return Object.entries({
            POLICE: policeInstanceEnabled,
            FIRE: fireInstanceEnabled,
            EMS: emsInstanceEnabled,
        })
            .filter(([, enabled]) => enabled)
            .map(([discipline]) => discipline as Discipline);
    }, [policeInstanceEnabled, fireInstanceEnabled, emsInstanceEnabled]);

    const [temporaryIncidentIds, setTemporaryIncidentIds] = useState<
        Record<Discipline, string | undefined>
    >(() => {
        if (isDraftEvent && !enhancementsEnabled) {
            return EMPTY_INCIDENT_IDS;
        }

        return getResolvedTemporaryIncidentIds(proQaTempIncidentStorageKeys);
    });

    useEffect(function migrateTemporaryIncidentIdsOnSave() {
        if (!proQaTempIncidentStorageKeys.savedEventStorageKey) {
            return;
        }

        setTemporaryIncidentIds((currentTemporaryIncidentIds) =>
            migrateTemporaryIncidentIdsToSavedEvent(
                proQaTempIncidentStorageKeys,
                currentTemporaryIncidentIds
            )
        );
    }, [proQaTempIncidentStorageKeys]);

    const updateTemporaryIncidentId = useCallback(
        (discipline: Discipline, id: string | undefined) => {
            setTemporaryIncidentIds(() =>
                setResolvedTemporaryIncidentId(proQaTempIncidentStorageKeys, discipline, id)
            );
            setCurrentTempId(id);
        },
        [proQaTempIncidentStorageKeys, setCurrentTempId]
    );

    if (isDraftEvent && !enhancementsEnabled) {
        return null;
    }

    if (isDraftEvent || !enhancementsEnabled) {
        return (
            <EventPanelFormOpenProQaButtons
                enabledDisciplines={enabledDisciplines}
                enhancementsEnabled={enhancementsEnabled}
                temporaryIncidentIds={temporaryIncidentIds}
                updateTemporaryIncidentId={updateTemporaryIncidentId}
                proQaTempIncidentStorageKeys={proQaTempIncidentStorageKeys}
            />
        );
    }

    return (
        <HStack data-testid="proqa-buttons">
            <EventPanelFormOpenProQaButtons
                enabledDisciplines={enabledDisciplines}
                enhancementsEnabled={enhancementsEnabled}
                temporaryIncidentIds={temporaryIncidentIds}
                updateTemporaryIncidentId={updateTemporaryIncidentId}
                proQaTempIncidentStorageKeys={proQaTempIncidentStorageKeys}
            />
            <EventPanelFormOpenProQaSummaryButton enabledDisciplines={enabledDisciplines} />
        </HStack>
    );
}
